import React, { useEffect, useMemo, useState } from 'react'
import { ClientOnboardingAssist } from '../../setup/ClientOnboardingAssist'
import type { ClientOnboardingAssistance } from '../../../services/clientOnboardingAssist'
import { FormModal } from './AdminUiPrimitives'
import {
  acceptOrganizationRelationshipRequest,
  createAssociatedClientCompany,
  createOrganizationConnectionInvite,
  createSystemOrganizationRelationship,
  listIncomingOrganizationRelationshipRequests,
  listOrganizationConnectionInvites,
  listOrganizationRelationships,
  listOutgoingOrganizationRelationshipRequests,
  redeemOrganizationConnectionInvite,
  rejectOrganizationRelationshipRequest,
  searchSystemOrganizations,
  updateOrganizationRelationship,
  updateOrganizationRelationshipPolicy,
} from '../../../services/access'
import type {
  AccessibleLocation,
  Organization,
  OrganizationConnectionInvite,
  OrganizationRelationship,
  OrganizationRelationshipStatus,
  RelationshipAccessEntry,
  RelationshipVisibilityPreset,
} from '../../../types/access'
import { formatDate, formatOrgType } from './adminPanelUtils'
import { useI18n } from '../../../i18n/I18nContext'

type TFunc = (text: string, vars?: Record<string, string | number>) => string

type AssociatedClientCompanyForm = {
  name: string
  slug: string
  tenantSlug: string
  businessEmail: string
  supervisorEmail: string
}

interface OrgRelationshipsPanelProps {
  organizations: Organization[]
  accessibleLocations: AccessibleLocation[]
  selectedOrgId: string
  canManageOrganizations: boolean
  canUsePlatformLinker?: boolean
  onSelectOrg: (orgId: string) => void
  relationshipAccess?: RelationshipAccessEntry[]
  initialRelationshipId?: string
}

const relationshipStatuses: OrganizationRelationshipStatus[] = ['pending', 'active', 'suspended', 'expired', 'revoked', 'rejected']
const visibilityPresets: RelationshipVisibilityPreset[] = ['directory_only', 'job_receiving_agency', 'location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner', 'custom']
const readablePreset = (preset: string) => preset.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
const titleCase = (value: string) => value.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
const boolLabel = (value: unknown) => value ? 'Yes' : 'No'

const relationshipTypeLabel = (type: string): string => {
  switch (type) {
    case 'company_agency': return 'Company - Agency'
    case 'agency_subcontractor': return 'Agency - Subcontractor'
    case 'agency_company': return 'Agency - Company'
    default: return titleCase(type)
  }
}

const connectionStateLabelText = (state: string): string => {
  switch (state) {
    case 'pending_invite': return 'Pending invite'
    case 'linked_unconfigured': return 'Linked, unconfigured'
    case 'policy_configured_no_locations': return 'Policy configured, no locations'
    case 'built_connection': return 'Connection built'
    case 'delegated_connection': return 'Delegated connection'
    case 'suspended': return 'Suspended'
    case 'revoked': return 'Revoked'
    case 'invalid_missing_endpoint': return 'Invalid: missing endpoint'
    case 'no_link': return 'No link'
    default: return titleCase(state)
  }
}

const connectionStateLabel = (relationship: OrganizationRelationship): string => {
  if (relationship.status === 'pending') return 'pending_invite'
  if (relationship.status === 'suspended') return 'suspended'
  if (relationship.status === 'revoked') return 'revoked'
  if (relationship.status !== 'active') return relationship.status || 'no_link'
  if (!relationship.visibility_policy_id && !relationship.visibility_preset) return 'linked_unconfigured'
  const hasDelegatedActions = Boolean(
    relationship.can_create_jobs_on_behalf
    || relationship.can_create_shifts_on_behalf
    || relationship.can_assign_workers_on_behalf
    || relationship.can_submit_timesheets
  )
  if ((relationship.location_grant_count || 0) > 0 && hasDelegatedActions) return 'delegated_connection'
  if ((relationship.location_grant_count || 0) === 0 && relationship.can_view_locations) return 'policy_configured_no_locations'
  return 'built_connection'
}

const connectionStateMessage = (state: string): string => {
  if (state === 'pending_invite') return 'Invite sent. Waiting for partner to accept.'
  if (state === 'linked_unconfigured') return 'Connected, but no visibility policy has been configured.'
  if (state === 'policy_configured_no_locations') return 'Permissions configured, but no locations granted.'
  if (state === 'built_connection') return 'Connection active.'
  if (state === 'delegated_connection') return 'Delegated workforce access active.'
  if (state === 'suspended') return 'Connection suspended. Future access is paused.'
  if (state === 'revoked') return 'Connection revoked. Historical records remain available where permitted.'
  if (state === 'invalid_missing_endpoint') return 'Connection references a missing organization and needs platform repair.'
  return 'Connection status unknown.'
}


const relationshipCompanyOrgId = (relationship: OrganizationRelationship) => (
  relationship.source_org_type === 'company' ? relationship.source_org_id : relationship.target_org_type === 'company' ? relationship.target_org_id : ''
)
const relationshipCompanyName = (relationship: OrganizationRelationship) => (
  relationship.source_org_type === 'company' ? relationship.source_org_name : relationship.target_org_type === 'company' ? relationship.target_org_name : 'Company'
)
const relationshipAgencyName = (relationship: OrganizationRelationship) => (
  relationship.source_org_type === 'agency' ? relationship.source_org_name : relationship.target_org_type === 'agency' ? relationship.target_org_name : 'Agency'
)
const isCompanyAgencyConnection = (relationship: OrganizationRelationship) => (
  relationship.relationship_type === 'company_agency' || relationship.relationship_type === 'agency_company'
)
const isCrossTenant = (relationship: OrganizationRelationship) => (
  relationship.tenant_scope === 'cross_tenant'
  || (relationship.source_tenant_id && relationship.target_tenant_id && relationship.source_tenant_id !== relationship.target_tenant_id)
)

export const OrgRelationshipsPanel: React.FC<OrgRelationshipsPanelProps> = ({
  organizations,
  accessibleLocations,
  selectedOrgId,
  canManageOrganizations,
  canUsePlatformLinker = false,
  onSelectOrg,
  relationshipAccess = [],
  initialRelationshipId,
}) => {
  const { t }: { t: TFunc } = useI18n()
  const [relationships, setRelationships] = useState<OrganizationRelationship[]>([])
  const [incomingRequests, setIncomingRequests] = useState<OrganizationRelationship[]>([])
  const [outgoingRequests, setOutgoingRequests] = useState<OrganizationRelationship[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [associatedInviteUrl, setAssociatedInviteUrl] = useState('')
  useEffect(() => setAssociatedInviteUrl(''), [selectedOrgId])
  const [statusDrafts, setStatusDrafts] = useState<Record<string, OrganizationRelationshipStatus>>({})
  const [invites, setInvites] = useState<OrganizationConnectionInvite[]>([])
  const [generatedCode, setGeneratedCode] = useState('')
  const [redeemCode, setRedeemCode] = useState('')
  const [platformAgencyQuery, setPlatformAgencyQuery] = useState('')
  const [platformCompanyQuery, setPlatformCompanyQuery] = useState('')
  const [platformAgencies, setPlatformAgencies] = useState<Organization[]>([])
  const [platformCompanies, setPlatformCompanies] = useState<Organization[]>([])
  const [platformAgencyId, setPlatformAgencyId] = useState('')
  const [platformCompanyId, setPlatformCompanyId] = useState('')
  const [policyRelationshipId, setPolicyRelationshipId] = useState('')
  const [onboardingAssistance,setOnboardingAssistance]=useState<ClientOnboardingAssistance>()
  const [assistRevision,setAssistRevision]=useState(0)
  useEffect(()=>setOnboardingAssistance(undefined),[selectedOrgId])
  const [wizardStep, setWizardStep] = useState(0)
  const [policyPreset, setPolicyPreset] = useState<RelationshipVisibilityPreset>('directory_only')
  const [policyLocationIds, setPolicyLocationIds] = useState<string[]>([])
  const [policyFlags, setPolicyFlags] = useState({
    canReceiveJobs: false,
    canCreateJobsOnBehalf: false,
    canCreateShiftsOnBehalf: false,
    canAssignWorkersOnBehalf: false,
    canSubmitTimesheets: false,
    canViewTimesheets: false,
    canViewPayRates: false,
    canViewBillRates: false,
    requiresCompanyJobApproval: true,
    requiresCompanyAssignmentApproval: true,
    requiresCompanyTimesheetApproval: true,
  })
  const [terminationAction, setTerminationAction] = useState<{ id: string; action: 'suspend' | 'revoke' } | null>(null)
  const [associatedCompanyForm, setAssociatedCompanyForm] = useState<AssociatedClientCompanyForm>({
    name: '',
    slug: '',
    tenantSlug: '',
    businessEmail: '',
    supervisorEmail: '',
  })

  const selectedOrg = organizations.find((org) => org.id === selectedOrgId)
  const requestTargetType = selectedOrg?.org_type === 'agency' ? 'company' : 'agency'
  const selectedPolicyRelationship = relationships.find((row) => row.id === policyRelationshipId)
  const policyCompanyOrgId = selectedPolicyRelationship ? relationshipCompanyOrgId(selectedPolicyRelationship) : ''

  const accessEntry = useMemo(
    () => policyRelationshipId ? relationshipAccess.find((entry) => entry.relationshipId === policyRelationshipId) : undefined,
    [policyRelationshipId, relationshipAccess],
  )
  const canManageSelectedPolicy = Boolean(accessEntry
    ? (accessEntry.canManagePolicy || accessEntry.canManageGrants)
    : (canUsePlatformLinker || selectedOrg?.id === policyCompanyOrgId || selectedOrg?.org_type === 'company'))
  const canTerminateSelected = Boolean(accessEntry ? accessEntry.canTerminate : (canManageOrganizations && selectedOrg?.org_type !== 'agency'))

  const policyLocations = accessibleLocations.filter((location) => (
    !policyCompanyOrgId
    || location.ownerOrgId === policyCompanyOrgId
    || location.owner_org_id === policyCompanyOrgId
    || location.companyOrgId === policyCompanyOrgId
    || location.company_org_id === policyCompanyOrgId
  ))

  useEffect(() => {
    if (!selectedOrgId) return
    let mounted = true
    setLoading(true)
    setError('')
    setRelationships([])
    setPolicyRelationshipId('')
    listOrganizationRelationships(selectedOrgId)
      .then((rows) => {
        if (!mounted) return
        setRelationships(rows)
        setStatusDrafts(Object.fromEntries(rows.map((row) => [row.id, row.status])))
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load organization relationships.')))
      .finally(() => mounted && setLoading(false))
    return () => { mounted = false }
  }, [selectedOrgId])

  useEffect(() => {
    if (!initialRelationshipId || relationships.length === 0) return
    const target = relationships.find((row) => row.id === initialRelationshipId)
    if (target) openPolicyEditor(target)
  }, [initialRelationshipId, relationships])

  useEffect(() => {
    if (!canManageOrganizations) {
      setIncomingRequests([])
      setOutgoingRequests([])
      return
    }
    let mounted = true
    Promise.all([
      listIncomingOrganizationRelationshipRequests(),
      listOutgoingOrganizationRelationshipRequests(),
    ])
      .then(([incoming, outgoing]) => {
        if (!mounted) return
        setIncomingRequests(incoming)
        setOutgoingRequests(outgoing)
      })
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load relationship requests.')))
    return () => { mounted = false }
  }, [canManageOrganizations, selectedOrgId])

  const refresh = async () => {
    if (!selectedOrgId) return
    const rows = await listOrganizationRelationships(selectedOrgId)
    setRelationships(rows)
    setStatusDrafts(Object.fromEntries(rows.map((row) => [row.id, row.status])))
    const [incoming, outgoing] = await Promise.all([
      listIncomingOrganizationRelationshipRequests(),
      listOutgoingOrganizationRelationshipRequests(),
    ])
    setIncomingRequests(incoming)
    setOutgoingRequests(outgoing)
    if (canManageOrganizations) {
      setInvites(await listOrganizationConnectionInvites(selectedOrgId))
    }
  }

  useEffect(() => {
    if (!selectedOrgId || !canManageOrganizations) {
      setInvites([])
      return
    }
    let mounted = true
    listOrganizationConnectionInvites(selectedOrgId)
      .then((rows) => mounted && setInvites(rows))
      .catch((err: any) => mounted && setError(err?.response?.data?.message || t('Unable to load invite codes.')))
    return () => { mounted = false }
  }, [canManageOrganizations, selectedOrgId])

  const handleRequestDecision = async (requestId: string, decision: 'accept' | 'reject') => {
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (decision === 'accept') {
        await acceptOrganizationRelationshipRequest(requestId)
        setMessage(t('Relationship request accepted.'))
      } else {
        await rejectOrganizationRelationshipRequest(requestId)
        setMessage(t('Relationship request rejected.'))
      }
      await refresh()
    } catch (err: any) {
      setError(err?.response?.data?.message || (decision === 'accept' ? t('Unable to accept relationship request.') : t('Unable to reject relationship request.')))
    } finally {
      setSaving(false)
    }
  }

  const handleStatusUpdate = async (relationship: OrganizationRelationship) => {
    if (!canManageOrganizations) return
    const status = statusDrafts[relationship.id] || relationship.status
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await updateOrganizationRelationship(relationship.id, { status })
      await refresh()
      setMessage(t('Relationship status updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update relationship status.'))
    } finally {
      setSaving(false)
    }
  }

  const handleTerminate = async () => {
    if (!terminationAction) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await updateOrganizationRelationship(terminationAction.id, { status: terminationAction.action === 'suspend' ? 'suspended' : 'revoked' })
      setTerminationAction(null)
      await refresh()
      setMessage(terminationAction.action === 'suspend' ? t('Connection suspended.') : t('Connection revoked. Historical records remain available where permitted.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || (terminationAction.action === 'suspend' ? t('Unable to suspend connection.') : t('Unable to revoke connection.')))
    } finally {
      setSaving(false)
    }
  }

  const applyPreset = (preset: RelationshipVisibilityPreset) => {
    setPolicyPreset(preset)
    setPolicyFlags((current) => ({
      ...current,
      canReceiveJobs: ['job_receiving_agency', 'location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canCreateJobsOnBehalf: ['delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canCreateShiftsOnBehalf: ['delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canAssignWorkersOnBehalf: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canSubmitTimesheets: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canViewTimesheets: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      requiresCompanyJobApproval: preset !== 'directory_only',
      requiresCompanyAssignmentApproval: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner', 'custom'].includes(preset),
      requiresCompanyTimesheetApproval: preset !== 'directory_only',
    }))
  }

  const openPolicyEditor = (relationship: OrganizationRelationship) => {
    const preset = (relationship.visibility_preset || 'directory_only') as RelationshipVisibilityPreset
    setPolicyRelationshipId(relationship.id)
    setPolicyPreset(preset)
    setPolicyLocationIds([])
    setPolicyFlags({
      canReceiveJobs: ['job_receiving_agency', 'location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canCreateJobsOnBehalf: ['delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canCreateShiftsOnBehalf: ['delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canAssignWorkersOnBehalf: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canSubmitTimesheets: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canViewTimesheets: ['location_scoped_supplier', 'delegated_staffing_partner', 'managed_workforce_partner'].includes(preset),
      canViewPayRates: Boolean(relationship.can_view_pay_rates),
      canViewBillRates: Boolean(relationship.can_view_bill_rates),
      requiresCompanyJobApproval: relationship.requires_company_job_approval ?? true,
      requiresCompanyAssignmentApproval: relationship.requires_company_assignment_approval ?? true,
      requiresCompanyTimesheetApproval: relationship.requires_company_timesheet_approval ?? true,
    })
    setWizardStep(0)
  }

  const handlePolicySave = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!policyRelationshipId || !canManageSelectedPolicy) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await updateOrganizationRelationshipPolicy(policyRelationshipId, {
        visibilityPreset: policyPreset,
        locationIds: policyLocationIds,
        ...policyFlags,
      })
      await refresh()
      setPolicyRelationshipId('')
      setMessage(t('Relationship policy updated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to update relationship policy.'))
    } finally {
      setSaving(false)
    }
  }

  const handleGenerateInvite = async () => {
    if (!selectedOrgId || !selectedOrg || !canManageOrganizations) {
      setError(t('Select an organization before generating an invite code.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const invite = await createOrganizationConnectionInvite(selectedOrgId, {
        targetOrganizationType: requestTargetType as 'agency' | 'company',
        expiresInDays: 14,
        autoAccept: false,
      })
      setGeneratedCode(invite.code || '')
      await refresh()
      setMessage(t('Invite code generated.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to generate invite code.'))
    } finally {
      setSaving(false)
    }
  }

  const handleRedeemInvite = async (event: React.FormEvent) => {
    event.preventDefault()
    const code = redeemCode.trim()
    if (!selectedOrgId) {
      setError(t('Select an organization before redeeming an invite code.'))
      return
    }
    if (!code) {
      setError(t('Enter an invite code before redeeming.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await redeemOrganizationConnectionInvite({ code, redeemerOrganizationId: selectedOrgId })
      setRedeemCode('')
      await refresh()
      setMessage(t('Invite code redeemed. The relationship is pending unless the invite allowed auto-accept.'))
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to redeem invite code.'))
    } finally {
      setSaving(false)
    }
  }

  const handlePlatformSearch = async () => {
    if (!canUsePlatformLinker) return
    setSaving(true)
    setError('')
    try {
      const [agencies, companies] = await Promise.all([
        searchSystemOrganizations({ type: 'agency', q: platformAgencyQuery }),
        searchSystemOrganizations({ type: 'company', q: platformCompanyQuery }),
      ])
      setPlatformAgencies(agencies)
      setPlatformCompanies(companies)
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to search global organizations.'))
    } finally {
      setSaving(false)
    }
  }

  const handlePlatformLink = async () => {
    const agency = platformAgencies.find((org) => org.id === platformAgencyId)
    const company = platformCompanies.find((org) => org.id === platformCompanyId)
    if (!agency || !company) {
      setError(t('Select one agency and one company.'))
      return
    }
    if (!agency.tenant_id) {
      setError(t('Selected agency is missing tenant context.'))
      return
    }
    if (!company.tenant_id) {
      setError(t('Selected company is missing tenant context.'))
      return
    }
    const agencyTenantId = agency.tenant_id
    const companyTenantId = company.tenant_id
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await createSystemOrganizationRelationship({
        sourceTenantId: agencyTenantId,
        sourceOrganizationId: agency.id,
        targetTenantId: companyTenantId,
        targetOrganizationId: company.id,
        relationshipType: 'company_agency',
        status: 'active',
      })
      setMessage(t('Global organization relationship created.'))
      await refresh()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to create global relationship.'))
    } finally {
      setSaving(false)
    }
  }

  const handleAssociatedClientCompanyCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedOrgId || selectedOrg?.org_type !== 'agency' || !canManageOrganizations) return
    if (!associatedCompanyForm.name.trim()) {
      setError(t('Client company name is required.'))
      return
    }
    if (!associatedCompanyForm.supervisorEmail.trim()) {
      setError(t('Client supervisor email is required.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    setAssociatedInviteUrl('')
    try {
      const result = await createAssociatedClientCompany(selectedOrgId, {
        assistance:onboardingAssistance,
        name: associatedCompanyForm.name.trim(),
        slug: associatedCompanyForm.slug.trim() || undefined,
        tenantSlug: associatedCompanyForm.tenantSlug.trim() || undefined,
        businessEmail: associatedCompanyForm.businessEmail.trim() || undefined,
        supervisor: {
          email: associatedCompanyForm.supervisorEmail.trim(),
        },
      })
      setAssociatedCompanyForm({
        name: '',
        slug: '',
        tenantSlug: '',
        businessEmail: '',
        supervisorEmail: '',
      })
      setOnboardingAssistance(undefined)
      setAssistRevision(previous=>previous+1)
      await refresh()
      setAssociatedInviteUrl(result.supervisor?.inviteUrl || '')
      setMessage(t('Company created. Share the supervisor invitation securely. The owner must accept before joining.'))

    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to create associated client company.'))
    } finally {
      setSaving(false)
    }
  }

  const wizardAgencyLabel = selectedPolicyRelationship
    ? (isCrossTenant(selectedPolicyRelationship) ? t('External agency') : t('Agency'))
    : t('Agency')
  const wizardCompanyLabel = selectedPolicyRelationship
    ? (isCrossTenant(selectedPolicyRelationship) ? t('External company') : t('Company'))
    : t('Company')
  const wizardRelationshipLabel = accessEntry?.isAgencyManagedProfile
    ? t('Legacy agency-managed profile')
    : (isCrossTenant(selectedPolicyRelationship || ({} as OrganizationRelationship)) ? t('Federated partner') : t('Workforce partner'))

  return (
    <div className="stack">
      <div className="admin-panel-toolbar">
        <div className="form-row admin-select-row">
          <label htmlFor="relationship-org">{t('Selected organization')}</label>
          <select id="relationship-org" className="input" value={selectedOrgId} onChange={(event) => onSelectOrg(event.target.value)}>
            {organizations.map((org) => <option key={org.id} value={org.id}>{org.name} ({t(formatOrgType(org.org_type))})</option>)}
          </select>
        </div>
        <p className="helper">{t('Relationships are loaded from the selected organization in either source or target direction.')}</p>
      </div>

      {selectedOrg?.org_type === 'agency' ? (
        <form className="panel workspace-panel admin-edit-panel stack" onSubmit={handleAssociatedClientCompanyCreate}>
          <h3 style={{ margin: 0 }}>{t('Onboard Associated Client Company')}</h3>
          <p className="helper">{t('Creates a dedicated company tenant, company organization, default site-supervisor role, and workforce-network relationship. This replaces legacy agency-managed client profiles.')}</p>
          {!canManageOrganizations ? <p className="helper">{t('Associated client company onboarding requires agency admin access.')}</p> : null}
          {canManageOrganizations?<fieldset disabled={saving}><ClientOnboardingAssist key={`${selectedOrgId}:${assistRevision}`} agencyOrgId={selectedOrgId}
            connections={relationships.map(relationship=>({id:relationship.id,label:relationshipCompanyName(relationship)||relationship.id}))}
            onDraft={draft=>{setOnboardingAssistance(draft);const name=draft?.companyProfileConfirmation?.fields.legalName||draft?.companyProfileConfirmation?.fields.tradingName;if(typeof name==='string')setAssociatedCompanyForm(previous=>({...previous,name}))}}
            onReviewPolicy={id=>{const relationship=relationships.find(row=>row.id===id);if(relationship)openPolicyEditor(relationship)}}/></fieldset>:null}
          <div className="admin-form-grid">
            <div className="form-row">
              <label htmlFor="associated-company-name">{t('Client company name')}</label>
              <input id="associated-company-name" className="input" value={associatedCompanyForm.name} onChange={(event) => setAssociatedCompanyForm({ ...associatedCompanyForm, name: event.target.value })} disabled={!canManageOrganizations || saving} />
            </div>
            <div className="form-row">
              <label htmlFor="associated-company-slug">{t('Company slug')}</label>
              <input id="associated-company-slug" className="input" value={associatedCompanyForm.slug} onChange={(event) => setAssociatedCompanyForm({ ...associatedCompanyForm, slug: event.target.value })} disabled={!canManageOrganizations || saving} />
            </div>
            <div className="form-row">
              <label htmlFor="associated-tenant-slug">{t('Tenant slug')}</label>
              <input id="associated-tenant-slug" className="input" value={associatedCompanyForm.tenantSlug} onChange={(event) => setAssociatedCompanyForm({ ...associatedCompanyForm, tenantSlug: event.target.value })} disabled={!canManageOrganizations || saving} />
            </div>
            <div className="form-row">
              <label htmlFor="associated-business-email">{t('Business email')}</label>
              <input id="associated-business-email" className="input" value={associatedCompanyForm.businessEmail} onChange={(event) => setAssociatedCompanyForm({ ...associatedCompanyForm, businessEmail: event.target.value })} disabled={!canManageOrganizations || saving} />
            </div>
            <div className="form-row">
              <label htmlFor="associated-supervisor-email">{t('Client supervisor email')}</label>
              <input id="associated-supervisor-email" className="input" value={associatedCompanyForm.supervisorEmail} onChange={(event) => setAssociatedCompanyForm({ ...associatedCompanyForm, supervisorEmail: event.target.value })} disabled={!canManageOrganizations || saving} />
            </div>
          </div>
          <p className="helper">{t('The supervisor signs in or sets up their own account through an invitation. Existing passwords and profiles remain under owner control.')}</p>
          <button type="submit" className="btn btn-primary" disabled={!canManageOrganizations || saving}>
            {saving ? t('Saving...') : t('Create Associated Client Company')}
          </button>
        </form>
      ) : null}

      <div className="admin-two-column">
        <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>{t('Invite code')}</h3>
          <button type="button" className="btn btn-primary" disabled={!canManageOrganizations || saving} onClick={handleGenerateInvite}>
            {t('Generate invite code')}
          </button>
          {generatedCode ? <input className="input" value={generatedCode} readOnly /> : null}
          {invites.length === 0 ? <p className="text-muted">{t('No invite codes yet.')}</p> : invites.slice(0, 5).map((invite) => (
            <div key={invite.id} className="admin-list-row">
              <span>{t('{type} invite ending {code}', { type: t(formatOrgType(invite.targetOrganizationType)), code: invite.displayCodeLast4 || '----' })}</span>
              <span className="status-pill">{t(titleCase(invite.status))}</span>
            </div>
          ))}
        </section>
        <form className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} onSubmit={handleRedeemInvite}>
          <h3 style={{ margin: 0 }}>{t('Redeem invite')}</h3>
          <input className="input" value={redeemCode} onChange={(event) => setRedeemCode(event.target.value)} placeholder={t('Paste invite code')} disabled={!canManageOrganizations} />
          <button type="submit" className="btn btn-primary" disabled={!canManageOrganizations || saving || !redeemCode.trim()}>
            {t('Redeem code')}
          </button>
        </form>
      </div>

      {canUsePlatformLinker ? (
        <section className="panel workspace-panel admin-edit-panel stack">
          <h3 style={{ margin: 0 }}>{t('Super Admin global linker')}</h3>
          <div className="admin-form-grid">
            <div className="form-row">
              <label htmlFor="platform-agency-search">{t('Agency search')}</label>
              <input id="platform-agency-search" className="input" value={platformAgencyQuery} onChange={(event) => setPlatformAgencyQuery(event.target.value)} />
            </div>
            <div className="form-row">
              <label htmlFor="platform-company-search">{t('Company search')}</label>
              <input id="platform-company-search" className="input" value={platformCompanyQuery} onChange={(event) => setPlatformCompanyQuery(event.target.value)} />
            </div>
          </div>
          <button type="button" className="btn btn-ghost" disabled={saving} onClick={handlePlatformSearch}>{t('Search global directory')}</button>
          <div className="admin-form-grid">
            <div className="form-row">
              <label htmlFor="platform-agency">{t('Agency')}</label>
              <select id="platform-agency" className="input" value={platformAgencyId} onChange={(event) => setPlatformAgencyId(event.target.value)}>
                <option value="">{t('Select agency')}</option>
                {platformAgencies.map((org: any) => <option key={org.id} value={org.id}>{org.name} ({org.tenant_name || t('Tenant selected')})</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="platform-company">{t('Company')}</label>
              <select id="platform-company" className="input" value={platformCompanyId} onChange={(event) => setPlatformCompanyId(event.target.value)}>
                <option value="">{t('Select company')}</option>
                {platformCompanies.map((org: any) => <option key={org.id} value={org.id}>{org.name} ({org.tenant_name || t('Tenant selected')})</option>)}
              </select>
            </div>
          </div>
          <button type="button" className="btn btn-primary" disabled={saving || !platformAgencyId || !platformCompanyId} onClick={handlePlatformLink}>{t('Create active relationship')}</button>
        </section>
      ) : null}

      <div className="admin-two-column">
        <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>{t('Incoming requests')}</h3>
          {incomingRequests.length === 0 ? <p className="text-muted">{t('No incoming requests.')}</p> : incomingRequests.map((request) => (
            <div key={request.id} className="admin-list-row">
              <span>{t('{source} requested {target}', { source: request.source_org_name || t('Source organization'), target: request.target_org_name || t('Target organization') })}</span>
              <span className="admin-row-actions">
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => handleRequestDecision(request.id, 'accept')}>{t('Accept')}</button>
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => handleRequestDecision(request.id, 'reject')}>{t('Reject')}</button>
              </span>
            </div>
          ))}
        </section>
        <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>{t('Outgoing requests')}</h3>
          {outgoingRequests.length === 0 ? <p className="text-muted">{t('No outgoing requests.')}</p> : outgoingRequests.map((request) => (
            <div key={request.id} className="admin-list-row">
              <span>{t('{source} requested {target}', { source: request.source_org_name || t('Source organization'), target: request.target_org_name || t('Target organization') })}</span>
              <span className="status-pill">{t(titleCase(request.status))}</span>
            </div>
          ))}
        </section>
      </div>

      {message ? <p className="helper" role="status">{message}</p> : null}
      {associatedInviteUrl ? <p className="helper"><a href={associatedInviteUrl}>{t('Open supervisor invitation')}</a> · {t('Site access still requires authorized location grants.')}</p> : null}
      {error ? <p className="error-text">{error}</p> : null}

      <section className="network-dashboard-section">
        <div>
          <h3>{selectedOrg?.org_type === 'agency' ? t('Client Companies') : t('Workforce Partners')}</h3>
          <p className="helper">
            {selectedOrg?.org_type === 'agency'
              ? t('Company connections visible to this agency. Company-side grants are read-only here unless your access grants policy management.')
              : t('Agency connections configured as workforce partners with company-controlled visibility, site grants, and delegated actions.')}
          </p>
        </div>
        <div className="admin-summary-grid">
          {relationships.map((relationship) => {
            const state = connectionStateLabel(relationship)
            const agencyName = relationshipAgencyName(relationship)
            const companyName = relationshipCompanyName(relationship)
            const isAgencyView = selectedOrg?.org_type === 'agency'
            const entry = relationshipAccess.find((e) => e.relationshipId === relationship.id)
            const canManage = entry ? (entry.canManagePolicy || entry.canManageGrants) : (canManageOrganizations && !isAgencyView)
            const canTerminate = entry ? entry.canTerminate : (canManageOrganizations && !isAgencyView)
            const federated = isCrossTenant(relationship)
            const isManaged = Boolean(entry?.isAgencyManagedProfile)
            return (
              <article key={relationship.id} className="panel stat-card network-metric-card">
                <span className="helper">
                  {isManaged ? t('Legacy agency-managed profile') : isAgencyView ? (federated ? t('External company') : t('Client company')) : (federated ? t('External agency') : t('Agency partner'))}
                </span>
                <strong>{isAgencyView ? companyName : agencyName}</strong>
                {federated ? <span className="status-pill status-pill--muted">{isAgencyView ? t('Federated partner') : t('External agency')}</span> : null}
                {isManaged ? <span className="status-pill status-pill--muted">{t('Legacy profile')}</span> : null}
                {!canManage && isAgencyView ? <span className="status-pill status-pill--muted">{t('Read-only')}</span> : null}
                <span className="status-pill">{t(connectionStateLabelText(state))}</span>
                <span className="helper">{t(connectionStateMessage(state))}</span>
                {isManaged ? <span className="helper">{t('Claim status: {status}', { status: t(titleCase(entry?.claimStatus || 'unclaimed')) })}</span> : null}
                {!isManaged ? (
                  <>
                    <span className="helper">{t('Relationship: {status} · Preset: {preset} · Sites: {count}', { status: t(titleCase(relationship.status)), preset: relationship.visibility_preset ? t(readablePreset(relationship.visibility_preset)) : t('No policy'), count: relationship.location_grant_count || 0 })}</span>
                    <span className="helper">{t('Receive jobs: {receive} · Create jobs: {create} · Shifts: {shifts}', { receive: t(boolLabel(relationship.can_receive_jobs)), create: t(boolLabel(relationship.can_create_jobs_on_behalf)), shifts: t(boolLabel(relationship.can_create_shifts_on_behalf)) })}</span>
                    <span className="helper">{t('Assign workers: {assign} · Timesheets: {timesheets} · Pay/Bill: {pay}/{bill}', { assign: t(boolLabel(relationship.can_assign_workers_on_behalf)), timesheets: t(boolLabel(relationship.can_view_timesheets)), pay: t(boolLabel(relationship.can_view_pay_rates)), bill: t(boolLabel(relationship.can_view_bill_rates)) })}</span>
                  </>
                ) : null}
                <div className="inline-group">
                  {canManage ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      data-testid="configure-relationship-btn"
                      disabled={relationship.status !== 'active' && !isManaged || !isCompanyAgencyConnection(relationship)}
                      onClick={() => openPolicyEditor(relationship)}
                    >{t('Configure')}</button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => openPolicyEditor(relationship)}
                    >{t('View scope')}</button>
                  )}
                  {canTerminate ? (
                    <>
                      <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setTerminationAction({ id: relationship.id, action: 'suspend' })}>{t('Suspend')}</button>
                      <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setTerminationAction({ id: relationship.id, action: 'revoke' })}>{t('Revoke')}</button>
                    </>
                  ) : null}
                </div>
              </article>
            )
          })}
          {relationships.length === 0 ? (
            <article className="panel stat-card network-metric-card">
              <strong>{selectedOrg?.org_type === 'agency' ? t('No client companies') : t('No linked agencies')}</strong>
              <span className="helper">{selectedOrg?.org_type === 'agency' ? t('No client companies are linked to this agency yet.') : t('No agencies are linked to this company yet.')}</span>
            </article>
          ) : null}
        </div>
      </section>

      <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
        {loading ? <p className="text-muted">{t('Loading relationships...')}</p> : (
          <table className="workspace-table">
            <thead><tr><th>{t('Source')}</th><th>{t('Target')}</th><th>{t('Type')}</th><th>{t('Status')}</th><th>{t('Connection')}</th><th>{t('Starts')}</th><th>{t('Ends')}</th><th>{t('Actions')}</th></tr></thead>
            <tbody>
              {relationships.map((relationship) => (
                <tr key={relationship.id}>
                  <td>
                    {relationship.source_org_name || t('Source organization')}
                    {isCrossTenant(relationship) ? <span className="status-pill status-pill--muted" style={{ marginLeft: '0.25rem' }}>{t('ext')}</span> : null}
                  </td>
                  <td>
                    {relationship.target_org_name || t('Target organization')}
                    {isCrossTenant(relationship) ? <span className="status-pill status-pill--muted" style={{ marginLeft: '0.25rem' }}>{t('ext')}</span> : null}
                  </td>
                  <td>{t(relationshipTypeLabel(relationship.relationship_type))}</td>
                  <td>
                    <select className="input admin-inline-select" value={statusDrafts[relationship.id] || relationship.status} onChange={(event) => setStatusDrafts({ ...statusDrafts, [relationship.id]: event.target.value as OrganizationRelationshipStatus })} disabled={!canManageOrganizations}>
                      {relationshipStatuses.map((status) => <option key={status} value={status}>{t(titleCase(status))}</option>)}
                    </select>
                  </td>
                  <td>
                    <span className="status-pill">{t(connectionStateLabelText(connectionStateLabel(relationship)))}</span>
                    <div className="helper">{t(connectionStateMessage(connectionStateLabel(relationship)))}</div>
                  </td>
                  <td>{formatDate(relationship.starts_at)}</td>
                  <td>{formatDate(relationship.ends_at)}</td>
                  <td>
                    <div className="stack" style={{ gap: '0.35rem' }}>
                      <span className="helper">{t('{preset} · {count} locations', { preset: relationship.visibility_preset ? t(readablePreset(relationship.visibility_preset)) : t('No policy'), count: relationship.location_grant_count || 0 })}</span>
                      <span className="inline-group">
                        <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations || saving} onClick={() => handleStatusUpdate(relationship)}>{t('Update')}</button>
                        <button type="button" className="btn btn-ghost" disabled={relationship.status !== 'active' || !isCompanyAgencyConnection(relationship)} onClick={() => openPolicyEditor(relationship)}>
                          {(() => {
                            const e = relationshipAccess.find((entry) => entry.relationshipId === relationship.id)
                            const canManage = e ? (e.canManagePolicy || e.canManageGrants) : (canManageOrganizations && selectedOrg?.org_type !== 'agency')
                            return canManage ? t('Configure') : t('View scope')
                          })()}
                        </button>
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
              {relationships.length === 0 ? <tr><td colSpan={8}>{selectedOrg?.org_type === 'agency' ? t('No client companies are linked to this agency yet.') : t('No agencies are linked to this company yet.')}</td></tr> : null}
            </tbody>
          </table>
        )}
      </div>

      <FormModal
        title={t('Configure Workforce Partner')}
        description={t('Review or configure the company-side sharing policy, granted sites, delegated actions, and approval rules.')}
        isOpen={Boolean(policyRelationshipId)}
        onClose={() => setPolicyRelationshipId('')}
        size="xl"
      >
        <form className="stack" onSubmit={handlePolicySave}>
          <div className="network-status-row">
            {['Overview', 'Connection type', 'Sites', 'Permissions', 'Approvals', 'Review'].map((label, index) => (
              <button key={label} type="button" className={`status-pill ${wizardStep === index ? '' : 'status-pill--muted'}`} onClick={() => setWizardStep(index)}>{index + 1}. {t(label)}</button>
            ))}
          </div>
          {selectedPolicyRelationship ? (
            <section className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
              <h4 style={{ margin: 0 }}>
                {wizardStep === 0 ? t('Overview') : wizardStep === 1 ? t('Connection type') : wizardStep === 2 ? t('Sites / locations') : wizardStep === 3 ? t('Permissions') : wizardStep === 4 ? t('Approval rules') : t('Review and activate')}
                {' '}
                <span className="helper" style={{ fontWeight: 'normal' }}>({wizardRelationshipLabel})</span>
              </h4>
              {!canManageSelectedPolicy ? (
                <p className="helper">{t('Read-only view. You can view this connection, but only the company or platform admin can modify access grants.')}</p>
              ) : null}
              {wizardStep === 0 ? (
                <div className="admin-form-grid">
                  <div className="form-row">
                    <label>{wizardCompanyLabel}</label>
                    <input className="input" value={relationshipCompanyName(selectedPolicyRelationship)} disabled />
                  </div>
                  {isCrossTenant(selectedPolicyRelationship) ? (
                    <div className="form-row">
                      <label>{t('Company tenant')}</label>
                    <input className="input" value={t('External tenant')} disabled />
                    </div>
                  ) : null}
                  <div className="form-row">
                    <label>{wizardAgencyLabel}</label>
                    <input className="input" value={relationshipAgencyName(selectedPolicyRelationship)} disabled />
                  </div>
                  {isCrossTenant(selectedPolicyRelationship) ? (
                    <div className="form-row">
                      <label>{t('Agency tenant')}</label>
                    <input className="input" value={t('External tenant')} disabled />
                    </div>
                  ) : null}
                  <div className="form-row">
                    <label>{t('Connection state')}</label>
                    <input className="input" value={t(connectionStateLabelText(connectionStateLabel(selectedPolicyRelationship)))} disabled />
                  </div>
                  {accessEntry?.isAgencyManagedProfile ? (
                    <p className="helper">{t('Legacy agency-managed profile. New client companies should be onboarded as Associated Client Companies with their own tenant.')}</p>
                  ) : isCrossTenant(selectedPolicyRelationship) ? (
                    <p className="helper">{t('This is a federated relationship. The company and agency exist in separate tenants. Policy is controlled by the company side.')}</p>
                  ) : null}
                </div>
              ) : null}
              {wizardStep === 1 ? (
                <div className="admin-form-grid">
                  <div className="form-row">
                    <label htmlFor="relationship-policy-preset">{t('Preset')}</label>
                    <select id="relationship-policy-preset" className="input" value={policyPreset} onChange={(event) => applyPreset(event.target.value as RelationshipVisibilityPreset)} disabled={!canManageSelectedPolicy}>
                      {visibilityPresets.map((preset) => <option key={preset} value={preset}>{t(readablePreset(preset))}</option>)}
                    </select>
                  </div>
                  <p className="helper">{t('Directory only builds the link without site access. Delegated and managed presets enable company-controlled job, shift, assignment, and timesheet actions.')}</p>
                </div>
              ) : null}
              {wizardStep === 2 ? (
                <div className="stack">
                  <p className="helper">{policyLocations.length ? t('Selected locations are granted to the agency as scope for the enabled permissions below.') : t('No locations are available in this scope. The agency can view policy, but no sites have been granted.')}</p>
                  <div className="admin-form-grid">
                    {policyLocations.map((location) => {
                      const checked = policyLocationIds.includes(location.id)
                      return (
                        <label key={location.id} className="panel workspace-panel stack" style={{ background: 'var(--surface)' }}>
                          <span className="inline-group" style={{ alignItems: 'center' }}>
                            <input type="checkbox" checked={checked} disabled={!canManageSelectedPolicy} onChange={(event) => setPolicyLocationIds(event.target.checked ? [...policyLocationIds, location.id] : policyLocationIds.filter((id) => id !== location.id))} />
                            <strong>{location.name || location.label || t('Granted location')}</strong>
                          </span>
                          <span className="helper">{t('Scoped: {value} · View/staff follows policy flags below', { value: checked ? t('Yes') : t('No') })}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ) : null}
              {wizardStep === 3 ? (
                <div className="admin-form-grid">
                  {[
                    ['canReceiveJobs', 'Can receive jobs'],
                    ['canCreateJobsOnBehalf', 'Can create jobs on behalf'],
                    ['canCreateShiftsOnBehalf', 'Can create shifts on behalf'],
                    ['canAssignWorkersOnBehalf', 'Can assign own workers'],
                    ['canSubmitTimesheets', 'Can submit timesheets'],
                    ['canViewTimesheets', 'Can view own worker timesheets'],
                    ['canViewPayRates', 'Can view pay rates'],
                    ['canViewBillRates', 'Can view bill rates'],
                  ].map(([key, label]) => (
                    <label key={key} className="inline-group" style={{ alignItems: 'center' }}>
                      <input type="checkbox" checked={Boolean((policyFlags as any)[key])} disabled={!canManageSelectedPolicy} onChange={(event) => setPolicyFlags({ ...policyFlags, [key]: event.target.checked })} />
                      {t(label)}
                    </label>
                  ))}
                  {!canManageSelectedPolicy ? <p className="helper">{t('Agency users cannot self-grant permissions. Only the company side can modify these flags.')}</p> : null}
                </div>
              ) : null}
              {wizardStep === 4 ? (
                <div className="admin-form-grid">
                  {[
                    ['requiresCompanyJobApproval', 'Company approval required for agency-created jobs'],
                    ['requiresCompanyAssignmentApproval', 'Company approval required for worker assignments'],
                    ['requiresCompanyTimesheetApproval', 'Company approval required for timesheets'],
                  ].map(([key, label]) => (
                    <label key={key} className="inline-group" style={{ alignItems: 'center' }}>
                      <input type="checkbox" checked={Boolean((policyFlags as any)[key])} disabled={!canManageSelectedPolicy} onChange={(event) => setPolicyFlags({ ...policyFlags, [key]: event.target.checked })} />
                      {t(label)}
                    </label>
                  ))}
                </div>
              ) : null}
              {wizardStep === 5 ? (
                <div className="stack">
                  <p><strong>{wizardAgencyLabel}:</strong> {relationshipAgencyName(selectedPolicyRelationship)}</p>
                  <p><strong>{wizardCompanyLabel}:</strong> {relationshipCompanyName(selectedPolicyRelationship)}</p>
                  <p className="helper">{t('Relationship scope is saved against this selected connection by the backend.')}</p>
                  <p className="helper">{t('Preset: {preset} · Granted sites: {count}', { preset: t(readablePreset(policyPreset)), count: policyLocationIds.length })}</p>
                  <p className="helper">{t('Jobs: receive {receive}, create {create} · Shifts: {shifts} · Assign workers: {assign}', { receive: t(boolLabel(policyFlags.canReceiveJobs)), create: t(boolLabel(policyFlags.canCreateJobsOnBehalf)), shifts: t(boolLabel(policyFlags.canCreateShiftsOnBehalf)), assign: t(boolLabel(policyFlags.canAssignWorkersOnBehalf)) })}</p>
                  <p className="helper">{t('Timesheets: submit {submit}, view {view} · Rates: pay {pay}, bill {bill}', { submit: t(boolLabel(policyFlags.canSubmitTimesheets)), view: t(boolLabel(policyFlags.canViewTimesheets)), pay: t(boolLabel(policyFlags.canViewPayRates)), bill: t(boolLabel(policyFlags.canViewBillRates)) })}</p>
                  <p className="helper">{t('Termination/revocation blocks future access while preserving historical records where permitted.')}</p>
                  {isCrossTenant(selectedPolicyRelationship) ? <p className="helper">{t('Federated: policy saves to relationship ID only. Tenant ownership is resolved by the backend.')}</p> : null}
                </div>
              ) : null}
            </section>
          ) : null}
          <div className="inline-group">
            <button type="button" className="btn btn-ghost" disabled={wizardStep === 0} onClick={() => setWizardStep(Math.max(0, wizardStep - 1))}>{t('Back')}</button>
            <button type="button" className="btn btn-secondary" disabled={wizardStep === 5} onClick={() => setWizardStep(Math.min(5, wizardStep + 1))}>{t('Next')}</button>
            {canManageSelectedPolicy ? (
              <button type="submit" className="btn btn-primary" disabled={saving || !canManageSelectedPolicy} data-testid="save-policy-btn">{saving ? t('Saving...') : t('Save policy')}</button>
            ) : null}
            {canTerminateSelected && selectedPolicyRelationship ? (
              <span className="inline-group">
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setTerminationAction({ id: selectedPolicyRelationship.id, action: 'suspend' })}>{t('Suspend')}</button>
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setTerminationAction({ id: selectedPolicyRelationship.id, action: 'revoke' })}>{t('Revoke')}</button>
              </span>
            ) : null}
          </div>
        </form>
      </FormModal>

      {terminationAction ? (
        <FormModal
          title={terminationAction.action === 'suspend' ? t('Suspend connection') : t('Revoke connection')}
          description={terminationAction.action === 'suspend'
            ? t('Suspending this connection will pause future access for the agency. Historical records remain available where permitted by your data retention policy.')
            : t('Revoking this connection will permanently block future access for the agency. Historical records (timesheets, assignments, punches) remain available where permitted.')}
          isOpen={true}
          onClose={() => setTerminationAction(null)}
          size="md"
        >
          <div className="stack">
            <p className="helper">
              {terminationAction.action === 'suspend'
                ? t('The agency will lose access to all granted sites and delegated abilities. You can reactivate the connection later.')
                : t('This action cannot be undone automatically. Historical workforce records are preserved per your data retention policy.')}
            </p>
            <div className="inline-group">
              <button type="button" className="btn btn-primary" disabled={saving} onClick={handleTerminate}>
                {saving ? t('Processing...') : terminationAction.action === 'suspend' ? t('Confirm suspend') : t('Confirm revoke')}
              </button>
              <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setTerminationAction(null)}>{t('Cancel')}</button>
            </div>
          </div>
        </FormModal>
      ) : null}
    </div>
  )
}
